import type { Amount, FinancialRate } from '@orus.eu/amount'
import {
  ProductQuoteV2Dimension,
  ProductQuoteV3Dimension,
  type RcProGuaranteeType,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import type { QuoteModelInput, RcphQuoteV3 } from '@orus.eu/pricing'
import { type RcphQuoteV2 } from '@orus.eu/quote'

export type RcProOptionActive = {
  isOption: boolean
  /**
   * Whether the quarantee is active or not, always true when it's an action (we could represent this
   * with typescript, but we don't because TSOA doesn't support it)
   */
  active: boolean
}

export type RcProGuaranteeQuote = RcProOptionActive & {
  name: RcProGuaranteeType
  label: string
} & (
    | {
        paymentRecurrence: 'monthly'
        yearlyBasePremium: Amount
        yearlyTaxes: Amount
        yearlyTotalPremium: Amount
        monthlyBasePremium: Amount
        monthlyTaxes: Amount
        monthlyTotalPremium: Amount
      }
    | {
        paymentRecurrence: 'yearly'
        yearlyBasePremium: Amount
        yearlyTaxes: Amount
        yearlyTotalPremium: Amount
      }
  )

export type FreelanceYearlyGuaranteeQuote = RcProGuaranteeQuote & { paymentRecurrence: 'yearly' }
export type YearlyFreelanceQuote = {
  orusFeeRate: FinancialRate
  yearlyBasePremium: Amount
  /**
   * Orus yearly fee, already included in the base premium
   */
  yearlyOrusFee: Amount
  /**
   * Total taxes
   */
  yearlyTaxes: Amount
  yearlyTotalPremium: Amount
  rcp: FreelanceYearlyGuaranteeQuote
  rce: FreelanceYearlyGuaranteeQuote
  pj: FreelanceYearlyGuaranteeQuote
  world: FreelanceYearlyGuaranteeQuote
  tdmi?: FreelanceYearlyGuaranteeQuote
  cyber?: FreelanceYearlyGuaranteeQuote
  cyberDommagesPe?: FreelanceYearlyGuaranteeQuote
  cyberDefaillanceFournisseur?: FreelanceYearlyGuaranteeQuote
  cyberDommagesTiers?: FreelanceYearlyGuaranteeQuote
  cyberFraudePiratage?: FreelanceYearlyGuaranteeQuote
}

type FreelanceMonthlyGuaranteeQuote = RcProGuaranteeQuote & { paymentRecurrence: 'monthly' }
export type MonthlyFreelanceQuote = {
  orusFeeRate: FinancialRate
  yearlyBasePremium: Amount
  /**
   * Orus yearly fee, already included in the base premium
   */
  yearlyOrusFee: Amount
  /**
   * Total taxes
   */
  yearlyTaxes: Amount
  yearlyTotalPremium: Amount
  monthlyBasePremium: Amount
  /**
   * Orus monthly fee, already included in the base premium
   */
  monthlyOrusFee: Amount
  /**
   * Monthly taxes for subsequent months
   */
  monthlyTaxes: Amount
  monthlyTotalPremium: Amount
  rcp: FreelanceMonthlyGuaranteeQuote
  rce: FreelanceMonthlyGuaranteeQuote
  pj: FreelanceMonthlyGuaranteeQuote
  world: FreelanceMonthlyGuaranteeQuote
  tdmi?: FreelanceMonthlyGuaranteeQuote
  cyber?: FreelanceMonthlyGuaranteeQuote
  cyberDommagesPe?: FreelanceMonthlyGuaranteeQuote
  cyberDefaillanceFournisseur?: FreelanceMonthlyGuaranteeQuote
  cyberDommagesTiers?: FreelanceMonthlyGuaranteeQuote
  cyberFraudePiratage?: FreelanceMonthlyGuaranteeQuote
}

export class RcphQuoteV2Dimension<NAME extends string, Quote extends RcphQuoteV2> extends ProductQuoteV2Dimension<
  NAME,
  Quote
> {}

export const rcphQuoteV2Dimension = new RcphQuoteV2Dimension({
  name: 'rcphQuoteV2',
  displayKeys: { name: 'dimension_name_rcph_quote_v2' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export class RcphQuoteV3Dimension<NAME extends string> extends ProductQuoteV3Dimension<
  NAME,
  1,
  QuoteModelInput,
  RcphQuoteV3
> {}

export const rcphQuoteV3Dimension = new RcphQuoteV3Dimension({
  name: 'rcphQuoteV3',
  displayKeys: { name: 'dimension_name_rcph_quote_v3' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcphQuoteExclusionDimension = new StringsWithDataEnumDimension({
  name: 'rcphQuoteExclusion',
  displayKeys: { name: 'dimension_name_rcph_quote_exclusion' },
  entries: [['pricing-issue', { label: 'Erreur lors du calcul du prix' }]],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
